import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, object, oneOfType, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router';
import unescape from 'lodash/unescape';

import CommonErrorBoundary from 'ps-components/Util/CommonErrorBoundary';
import CompetitiveSet from './CompetitiveSet/CompetitiveSet';
import CrowdSourcedCard from './CrowdSourcedCard/CrowdSourcedCard';
import Education from 'ps-components/Modules/AddJob/Education';
import EllipsisText from 'ps-components/EllipsisText/EllipsisText';
import Experience from 'ps-components/Modules/AddJob/Experience';
import JobTitleMatches from 'ps-components/Modules/AddJob/JobTitleMatches';
import JobTitleSearch from 'ps-components/Modules/AddJob/JobTitleSearch';
import Responsibilities from 'ps-components/Modules/AddJob/Responsibilities';
import SkillsAndCertifications from 'ps-components/Modules/AddJob/SkillsAndCertifications';
import SlideoutHeaderButton from 'ps-components/Slideout/Header/SlideoutHeaderButton';
import WorkLocationInput from 'ps-components/Input/WorkLocationInput/WorkLocationInput';
import { Alert } from 'ps-design';

import { saveProfilesAnalyzed } from 'ps-components/Modules/AddJob/reducers/jobDetails';
import {
  cancelEditJobTitle,
  clearAddJobState,
  deletePayscaleJobDetails,
  editJobSlideoutLoad,
  getAllReports,
  jobLocationSearch,
  launchEditJobSlideout,
  saveCareerLevel,
  saveJob,
  saveJobAndUpdatePay,
  selectJob,
  setInitialJobDetails,
  titleJobSearch,
  updatePayReport
} from 'ps-components/Modules/AddJob/reducers/addJob';
import { certificationsSearch, getJobCerts } from 'ps-components/Modules/AddJob/reducers/jobCertifications';
import {
  assignCompetitiveSet,
  loadJobDetails,
  showEditSurveys,
  surveyPriceLocation
} from 'ps-components/Modules/AddJob/reducers/jobSurveyList';
import { getJobSkills, skillsSearch } from 'ps-components/Modules/AddJob/reducers/skillsByJobList';
import { deleteMatch } from '../MarketPricing/MarketPricingActions';
import {
  changeSetValue,
  closeEditSet,
  deleteSet,
  editSet,
  getCompSets,
  getIndustryOptions,
  getOrgType,
  saveSet
} from 'ps-components/Modules/CompetitiveSets/reducers/competitiveSets';
import './CrowdSourcedMatch.scss';

const COMPETITIVE_SET_TEXT =
  'A Competitive Set is selection of compensable factor values that allow a user to further refine how the CrowdSource engine calculates a market rate. For example, by including a specific Organization Type and an Industry, the Crowdsource engine will incorporate individual profiles that resemble those characteristics into calculating a market value. That is why you will see different market values for the same survey job based on the make-up of your competitive set. It is comparable to how traditional surveys calculate market values based on data cuts but provides for more accurate predictions specific to competitive markets.';
const SLIDEOUT_HEADER_TEXT = 'Add PayScale Crowd-sourced Match to This Job';

export class CrowdSourcedMatch extends Component {
  static propTypes = {
    actions: shape({
      assignCompetitiveSet: func,
      cancelEditJobTitle: func,
      certificationsSearch: func,
      changeSetValue: func,
      clearAddJobState: func,
      closeEditSet: func,
      deleteMatch: func,
      deletePayscaleJobDetails: func,
      deleteSet: func,
      launchEditJobSlideout: func,
      editSet: func,
      getAllReports: func,
      getCompSets: func,
      getIndustryOptions: func,
      getJobCerts: func,
      getJobSkills: func,
      getOrgType: func,
      jobLocationSearch: func,
      loadJobDetails: func,
      saveCareerLevel: func,
      saveJob: func,
      saveJobAndUpdatePay: func,
      saveSet: func,
      selectJob: func,
      setInitialJobDetails: func,
      showEditSurveys: func,
      skillsSearch: func,
      surveyPriceLocation: func,
      titleJobSearch: func,
      updatePayReport: func
    }),
    careerLevel: string,
    careerLevels: arrayOf(shape({ label: string, value: string })),
    compSetsList: arrayOf(
      shape({
        company_type: string,
        competitive_set_name: string,
        competitive_set_pk: number,
        creation_time: string,
        creator_user_id: oneOfType([number, string]),
        employees_count: number,
        government_contractor: bool,
        industry: arrayOf(string),
        last_modified_time: oneOfType([string, number]),
        last_modifying_user_id: oneOfType([string, number]),
        total_revenue: number
      })
    ),
    compSetsLoaded: bool,
    compSetsLoading: bool,
    currentEditSet: oneOfType([
      bool,
      shape({
        competitive_set_pk: number,
        competitive_set_name: string,
        creator_user_id: oneOfType([string, number]),
        creation_time: string,
        last_modifying_user_id: oneOfType([string, number]),
        last_modified_time: oneOfType([string, number]),
        company_type: string,
        employees_count: number,
        total_revenue: number,
        industry: arrayOf(string),
        government_contractor: bool
      })
    ]),
    editJobTitleMode: bool,
    formSections: shape({
      showEducation: bool,
      showExperience: bool,
      showResponsibilities: bool,
      showSkillsCerts: bool
    }),
    HighestDegreeEarnedList: arrayOf(shape({ label: string, value: string })),
    jobCertifications: shape({
      allCerts: arrayOf(shape({ answer: string, count: number, percentageChange: number })),
      certsSearchItems: arrayOf(shape({ answer: string, count: number, percentageChange: number }))
    }),
    jobIdList: arrayOf(number),
    jobSurveyMatchList: array,
    jobSurveyMatchLoaded: bool,
    jobSurveyMatchLoading: bool,
    jobTitles: arrayOf(
      shape({
        ValueID: number,
        JobTitle: string,
        Tasks: arrayOf(string),
        TitleVariations: arrayOf(string),
        Frequency: number,
        Score: number
      })
    ),
    noJobCode: bool,
    orgTypes: arrayOf(shape({ label: string, value: string })),
    reports: object,
    saveProfilesAnalyzedDisplay: string,
    savingCompSet: bool,
    searchLoaded: bool,
    searchLoading: bool,
    selectedJobId: oneOfType([number, string]),
    selectedJobTitle: string,
    skillsByJobList: shape({
      allSkills: arrayOf(shape({ answer: string, count: number, percentageChange: number })),
      skillsSearchItems: arrayOf(shape({ answer: string, count: number, percentageChange: number }))
    }),
    suggestCareer: arrayOf(string),
    replacementTokens: arrayOf(
      shape({
        token: string,
        value: string
      })
    ),
    useJobCodeKey: bool,
    validation: shape({
      isValid: bool,
      noChange: bool
    }),
    workLocation: string,
    workLocationExact: string
  };

  constructor(props) {
    super(props);

    this.state = {
      showAlert: false,
      showJobCards: false,
      showJobSearch: false,
      showPopover: false
    };

    this.closeAlert = this.closeAlert.bind(this);
    this.closeSlideout = this.closeSlideout.bind(this);
    this.deleteMatch = this.deleteMatch.bind(this);
    this.getChartData = this.getChartData.bind(this);
    this.getReplacementToken = this.getReplacementToken.bind(this);
    this.handleJobClick = this.handleJobClick.bind(this);
    this.handleWorkLocationChange = this.handleWorkLocationChange.bind(this);
    this.loadDetails = this.loadDetails.bind(this);
    this.renderPopover = this.renderPopover.bind(this);
    this.searchForNewMatch = this.searchForNewMatch.bind(this);
    this.toggleCards = this.toggleCards.bind(this);
    this.toggleDetails = this.toggleDetails.bind(this);
    this.togglePopover = this.togglePopover.bind(this);
    this.toggleSearch = this.toggleSearch.bind(this);
    this.triggerAlert = this.triggerAlert.bind(this);
  }

  componentDidMount() {
    const { actions, hideSlideout, orgJobId, slideout } = this.props;

    if (!orgJobId && slideout.open) {
      hideSlideout();
    } else {
      this.loadDetails();
      actions.getOrgType();
    }
  }

  componentDidUpdate(prevProps) {
    const { showJobCards } = this.state;
    const {
      actions,
      jobSurveyMatchList,
      orgJobId,
      refreshJobTabData,
      searchLoading,
      searchLoaded,
      selectedJobId,
      slideout,
      subsite,
      workLocation
    } = this.props;
    const crowdSourcedSurveyCode = `PAY-${subsite.toUpperCase()}`;
    if (slideout.open && !prevProps.slideout.open) {
      this.loadDetails();
    }

    if (!slideout.open && prevProps.slideout.open) {
      if (refreshJobTabData) refreshJobTabData();
      actions.clearAddJobState();
    }

    if (selectedJobId !== prevProps.selectedJobId && !searchLoading && searchLoaded) {
      this.toggleDetails();
    }

    if (
      jobSurveyMatchList?.find(match => match.survey_code.includes(crowdSourcedSurveyCode)) &&
      !prevProps.jobSurveyMatchList?.find(match => match.survey_code.includes(crowdSourcedSurveyCode))
    ) {
      actions.loadJobDetails(orgJobId);
    }

    if (
      workLocation === undefined &&
      jobSurveyMatchList?.find(match => match.survey_code.includes(crowdSourcedSurveyCode)) &&
      !showJobCards &&
      !searchLoading
    ) {
      this.searchForNewMatch();
    }
  }

  closeAlert() {
    this.setState({ showAlert: false });
  }

  closeSlideout() {
    const { actions, hideSlideout, refreshJobTabData } = this.props;

    hideSlideout();
    actions.clearAddJobState();
    if (refreshJobTabData) refreshJobTabData();
  }

  deleteMatch = async triggerNewSearch => {
    const {
      actions,
      benchmarkYear,
      jobDetails,
      jobSurveyMatchList,
      orgJobCurrency,
      orgJobId,
      refreshJobTabData,
      subsite,
      workLocation
    } = this.props;
    const crowdSourcedSurveyCode = `PAY-${subsite.toUpperCase()}`;
    const crowdSourcedCut = jobSurveyMatchList.find(match => match.survey_code.includes(crowdSourcedSurveyCode));
    const resetWorkLocation = orgJobCurrency === 'USD' ? 'United States (Natl)' : 'Canada (Natl)';
    const hasLocationSet = jobDetails?.surveyPriceLocation && jobDetails?.surveyPriceLocation !== resetWorkLocation;
    const hasAssignedCompSet = crowdSourcedCut?.competitive_set_pk;
    const deleteActions = [];

    if (crowdSourcedCut)
      deleteActions.push(actions.deleteMatch(orgJobId, [crowdSourcedCut.cut_id], null, benchmarkYear));
    if (hasLocationSet)
      deleteActions.push(this.handleWorkLocationChange({ value: resetWorkLocation, label: resetWorkLocation }));
    if (hasAssignedCompSet) deleteActions.push(actions.assignCompetitiveSet({ org_job_id: orgJobId }));
    if (triggerNewSearch) deleteActions.push(this.searchForNewMatch());
    if (refreshJobTabData) deleteActions.push(refreshJobTabData());

    const results = await Promise.all([...deleteActions, actions.deletePayscaleJobDetails(orgJobId)]);

    return results;
  };

  getChartData() {
    const { orgJobId, reports } = this.props;
    const {
      percentile10,
      percentile25,
      percentile50,
      percentile75,
      percentile90
    } = reports.payReport.reports.basePayReport;
    const highVariance = false;
    const matchId = orgJobId ? orgJobId.toString() : '';
    const payRange = [percentile25, percentile50, percentile75];
    const payRangeMin = percentile10;
    const payRangeMax = percentile90;

    return { highVariance, matchId, payRange, payRangeMin, payRangeMax };
  }

  getReplacementToken() {
    const { replacementTokens } = this.props;

    return replacementTokens.find(datum => datum.token === 'job_code_key_text').value;
  }

  handleJobClick = async (jobId, jobTitle, tasks, titleVariations, titleSelected, row) => {
    const {
      actions,
      editJobTitleMode,
      orgJobCode,
      jobDescription,
      noJobCode,
      orgJobId,
      orgJobTitle,
      searchLoaded,
      searchLoading,
      selectedJobTitle,
      workLocationExact,
      orgJobCodeKey
    } = this.props;

    this.deleteMatch().then(async resp => {
      const jobDetails = {
        editJobTitleMode,
        jobDescription,
        jobDetails,
        noJobCode,
        orgJobId,
        orgJobTitle,
        searchLoaded,
        searchLoading,
        selectedJobTitle,
        workLocationExact,
        PayscaleJobTitle: jobTitle,
        Tasks: tasks,
        TitleVariations: titleVariations,
        workLocation: workLocationExact,
        jobCode: orgJobCode
      };
      const newMatchActions = [];

      newMatchActions.push(actions.selectJob(jobId, jobTitle, workLocationExact));
      newMatchActions.push(actions.setInitialJobDetails(jobDetails));
      newMatchActions.push(
        actions.getAllReports({
          jobTitle: jobTitle,
          orgJobId: orgJobId,
          orgJobTitle: orgJobTitle,
          orgJobCode: orgJobCode,
          noJobCode: noJobCode,
          workLocation: workLocationExact,
          jobDetails: jobDetails,
          existingJob: editJobTitleMode,
          jobDescription: jobDescription,
          orgJobCodeKey
        })
      );
      newMatchActions.push(actions.getJobSkills({ params: { title: jobTitle } }));
      newMatchActions.push(actions.getJobCerts({ params: { title: jobTitle } }));
      await Promise.all([newMatchActions]).then(() => this.triggerAlert());
    });
  };

  handleWorkLocationChange(event) {
    const { actions, orgJobId } = this.props;

    actions.surveyPriceLocation({
      org_job_id: orgJobId,
      surveyPriceLocation: event.label
    });
  }

  loadDetails() {
    const { actions, orgJobCode, orgJobCurrency, orgJobId, orgJobTitle, subsite } = this.props;
    const crowdSourcedSurveyCode = `PAY-${subsite.toUpperCase()}`;
    actions.showEditSurveys(orgJobId).then(res => {
      const hasPayscaleMatch = !!res?.find(match => match.survey_code.includes(crowdSourcedSurveyCode));

      if (hasPayscaleMatch) {
        actions.loadJobDetails(orgJobId).then(res => {
          actions.launchEditJobSlideout({ details: res });
          actions.editJobSlideoutLoad();
          this.setState({ showJobCards: false, showSearch: false });
        });
      } else {
        const workLocation = orgJobCurrency === 'USD' ? 'United States (Natl)' : 'Canada (Natl)';
        const titleSearchParams = {
          params: {
            orgJobId: orgJobId,
            title: orgJobTitle,
            workLocation: workLocation,
            jobCode: orgJobCode,
            noJobCode: false
          }
        };

        this.setState({ showJobCards: true });
        actions.titleJobSearch(titleSearchParams);
      }
    });
  }

  renderPopover() {
    return (
      <div className="popover">
        <div className="popover__close">
          <i className="icon-cancel" onClick={this.togglePopover} />
        </div>
        <div className="popover__text">
          <h4 className="popover__text-header">What is a Competitive Set?</h4>
          {COMPETITIVE_SET_TEXT}
        </div>
      </div>
    );
  }

  searchForNewMatch() {
    const { actions, orgJobCode, orgJobCurrency, orgJobId, orgJobTitle, workLocation } = this.props;
    const selectedWorkLocation = workLocation
      ? workLocation
      : orgJobCurrency === 'USD'
      ? 'United States (Natl)'
      : 'Canada (Natl)';
    const titleSearchParams = {
      params: {
        jobCode: orgJobCode,
        noJobCode: false,
        orgJobId: orgJobId,
        title: orgJobTitle,
        workLocation: selectedWorkLocation
      }
    };

    this.setState({ showJobCards: true });
    actions.titleJobSearch(titleSearchParams);
  }

  toggleCards() {
    const { showJobCards, showJobSearch } = this.state;

    this.setState({ showJobCards: !showJobCards, showJobSearch: !showJobSearch });
  }

  toggleDetails(e) {
    const { actions } = this.props;

    this.setState({ showJobCards: false, showJobSearch: false });
    if (e?.target?.id === 'cancel-job-search') actions.cancelEditJobTitle();
  }

  togglePopover() {
    const { showPopover } = this.state;

    this.setState({ showPopover: !showPopover });
  }

  toggleSearch() {
    const { showJobCards, showJobSearch } = this.state;

    this.setState({ showJobSearch: !showJobSearch, showJobCards: !showJobCards });
  }

  triggerAlert() {
    this.toggleDetails();
    this.setState({ showAlert: true }, () => {
      setTimeout(this.closeAlert, 4000);
    });
  }

  render() {
    const {
      actions,
      careerLevel,
      careerLevels,
      compSetsList,
      compSetsLoaded,
      compSetsLoading,
      compSetOrgTypes,
      currentEditSet,
      editJobTitleMode,
      formSections,
      HighestDegreeEarnedList,
      jobCertifications,
      jobDescription,
      jobDetails,
      jobIdList,
      jobSurveyMatchList,
      jobSurveyMatchLoaded,
      jobSurveyMatchLoading,
      jobTitles,
      noJobCode,
      orgJobCode,
      orgJobCurrency,
      orgJobCodeKey,
      orgJobId,
      orgJobTitle,
      orgTypes,
      refreshJobTabData,
      reports,
      saveProfilesAnalyzedDisplay,
      savingCompSet,
      searchLoaded,
      searchLoading,
      selectedJobTitle,
      skillsByJobList,
      slideout,
      subsite,
      suggestCareer,
      useJobCodeKey,
      validation,
      workLocation,
      workLocationExact
    } = this.props;
    const { showAlert, showJobCards, showJobSearch, showPopover } = this.state;
    const isMP = true;
    const jobCodeKeyDisplay = orgJobCodeKey === ' ' ? 'Not Set' : orgJobCodeKey;
    let rangeChartData;
    const showCrowdSourcedCard = !showJobCards && !showJobSearch;
    const showEducationInput = formSections.showEducation;
    const showExperienceInput = formSections.showExperience;
    const showResponsibilitiesInputs = formSections.showResponsibilities && reports;
    const showSkillsCertsInputs = formSections.showSkillsCerts;
    const selectedWorkLocation = jobDetails?.surveyPriceLocation
      ? jobDetails?.surveyPriceLocation
      : orgJobCurrency === 'USD'
      ? 'United States (Natl)'
      : 'Canada (Natl)';
    if (reports?.payReport?.reports?.basePayReport) rangeChartData = this.getChartData();
    const crowdSourcedSurveyCode = `PAY-${subsite.toUpperCase()}`;
    const crowdSourcedCut = jobSurveyMatchList?.find(match => match.survey_code.includes(crowdSourcedSurveyCode));

    return (
      <CommonErrorBoundary>
        <React.Fragment>
          {showAlert && (
            <Alert
              className="pxl-alert-success crowd-sourced__alert"
              message="Match has been added to pricing."
              level="success"
              close={true}
              onClickClose={this.closeAlert}
            />
          )}
          <div className="crowd-sourced__header">
            <section className="slideout-header__job">
              <div className="header-title">{!showCrowdSourcedCard && SLIDEOUT_HEADER_TEXT}</div>
              <div className="job-title__containter">
                <h3 className="job-title">
                  <EllipsisText maxChar={60} text={unescape(orgJobTitle)} />
                </h3>
              </div>
              <div className="job-information">
                <div className="job-information__container">
                  <label>Job Code</label>
                  <span className="info info_truncated">
                    <EllipsisText maxChar={19} text={orgJobCode} />
                  </span>
                </div>
                {useJobCodeKey && orgJobCodeKey && (
                  <div className="job-information__container">
                    <label>{this.getReplacementToken()}</label>
                    <span className="info info_truncated">
                      <EllipsisText maxChar={19} text={jobCodeKeyDisplay} />
                    </span>
                  </div>
                )}
                <div className="job-information__container">
                  <label>Currency</label>
                  <span className="info">{orgJobCurrency}</span>
                </div>
              </div>
            </section>
            <section className="slideout-header__actions">
              <SlideoutHeaderButton iconClass="icon-cancel" onClick={this.closeSlideout} />
            </section>
          </div>
          <div className="crowd-sourced__body">
            {showCrowdSourcedCard && (
              <React.Fragment>
                <section className="form-section">
                  <CrowdSourcedCard
                    crowdSourcedCut={crowdSourcedCut}
                    deleteMatch={this.deleteMatch}
                    isLoading={jobSurveyMatchLoading || Object.keys(reports)?.length === 0}
                    isMP={isMP}
                    isSlideoutOpen={slideout.open}
                    jobDescription={jobDetails?.Tasks?.join(' ')}
                    jobDetails={{
                      jobIdList: jobIdList,
                      details: {
                        payscale_job_info: {
                          saveProfilesAnalyzed: saveProfilesAnalyzedDisplay
                        }
                      }
                    }}
                    orgJobCurrency={orgJobCurrency}
                    rangeChartData={rangeChartData}
                    saveProfilesAnalyzed={actions.saveProfilesAnalyzed}
                    searchForNewMatch={this.searchForNewMatch}
                    selectedJobTitle={selectedJobTitle}
                  />
                </section>
                <h3 className="compensable-factors__title">Compensable Factors</h3>
                <section className="form-section">
                  <h4 className="form-section__header">Data Cuts</h4>
                  <div className="form-section__inputs">
                    <div className="form-section__input">
                      <label className="add-job__form-headers">Work location</label>
                      <WorkLocationInput
                        borderColor="black"
                        disabled={false}
                        onChange={this.handleWorkLocationChange}
                        orgJobCurrency={orgJobCurrency}
                        pageSpecificClasses="zindex"
                        search={actions.jobLocationSearch}
                        workLocationExact={selectedWorkLocation}
                      />
                    </div>
                    <div className="form-section__input">
                      <div className="add-job__form-headers-container">
                        <label className="add-job__form-headers">Competitive Sets</label>
                        <div className="form-section__popover">
                          <a className="form-section__popover-link" onClick={this.togglePopover}>
                            What is a Competitive Set?
                          </a>
                          {showPopover && this.renderPopover()}
                        </div>
                      </div>
                      <CompetitiveSet
                        assignCompetitiveSet={actions.assignCompetitiveSet}
                        changeSetValue={actions.changeSetValue}
                        closeEditSet={actions.closeEditSet}
                        compSetsLoaded={compSetsLoaded}
                        compSetsLoading={compSetsLoading}
                        currentEditSet={currentEditSet}
                        deleteSet={actions.deleteSet}
                        editSet={actions.editSet}
                        getCompSets={actions.getCompSets}
                        getIndustryOptions={actions.getIndustryOptions}
                        isSlideoutOpen={slideout.open}
                        items={compSetsList}
                        jobSurveyMatchList={jobSurveyMatchList}
                        jobSurveyMatchLoaded={jobSurveyMatchLoaded}
                        jobSurveyMatchLoading={jobSurveyMatchLoading}
                        orgJobId={orgJobId}
                        orgTypes={orgTypes}
                        saveSet={actions.saveSet}
                        subsite={subsite}
                        validation={validation}
                      />
                    </div>
                  </div>
                </section>
                <section className="form-section">
                  <h4 className="form-section__header">Experience</h4>
                  {showExperienceInput && (
                    <Experience
                      careerLevel={careerLevel}
                      careerLevels={careerLevels}
                      display={formSections.showExperience}
                      isMP={isMP}
                      jobCode={orgJobCode}
                      orgJobId={orgJobId}
                      orgJobTitle={orgJobTitle}
                      saveCareerLevel={actions.saveCareerLevel}
                      saveJobAndUpdatePay={actions.saveJobAndUpdatePay}
                      showYearsExperienceLabelUpdate={true}
                      suggestCareer={suggestCareer}
                      workLocationExact={workLocationExact}
                      yearsExperience={parseInt(jobDetails?.YearsExperience)}
                      yearsExperienceReport={reports?.yearsExperienceReport?.report?.results}
                      yearsMinimum={parseInt(jobDetails?.YearsMinimum)}
                    />
                  )}
                </section>
                <section className="form-section">
                  <h4 className="form-section__header">Education</h4>
                  {showEducationInput && (
                    <Education
                      display={formSections.showEducation}
                      highestDegreeEarned={jobDetails?.HighestDegreeEarned}
                      highestDegreeEarnedList={HighestDegreeEarnedList}
                      isMP={isMP}
                      jobCode={orgJobCode}
                      majorSpecialty={jobDetails?.MajorSpecialty}
                      orgJobId={orgJobId}
                      orgJobTitle={orgJobTitle}
                      saveJobAndUpdatePay={actions.saveJobAndUpdatePay}
                      workLocationExact={workLocationExact}
                    />
                  )}
                </section>
                <section className="form-section">
                  <h4 className="form-section__header">Skills and Certifications</h4>
                  {showSkillsCertsInputs && (
                    <SkillsAndCertifications
                      addJob={{
                        jobCode: orgJobCode,
                        jobDetails,
                        orgJobId,
                        orgJobTitle,
                        workLocationExact: workLocationExact
                      }}
                      certificationsSearch={actions.certificationsSearch}
                      display={formSections.showSkillsCerts}
                      isMP={isMP}
                      jobCertifications={jobCertifications}
                      saveJob={actions.saveJob}
                      saveJobAndUpdatePay={actions.saveJobAndUpdatePay}
                      skillsByJobList={skillsByJobList}
                      skillsSearch={actions.skillsSearch}
                      updatePayReport={actions.updatePayReport}
                    />
                  )}
                </section>
                <section className="form-section">
                  <h4 className="form-section__header">Job and Management Responsibilities</h4>
                  {showResponsibilitiesInputs && (
                    <Responsibilities
                      addJobDetails={jobDetails}
                      assignEvaluateWork={jobDetails.assignEvaluateWork}
                      display={formSections.showResponsibilities}
                      hireEmployees={jobDetails.hireEmployees}
                      isMP={isMP}
                      jobCode={orgJobCode}
                      JobResponsibilities={jobDetails.JobResponsibilities}
                      ManagementRole={jobDetails?.ManagementRole === 'Yes'}
                      mentorAndAdvise={jobDetails.mentorAndAdvise}
                      NumberSupervised={jobDetails.NumberSupervised}
                      orgJobCode={orgJobCode}
                      orgJobId={orgJobId}
                      orgJobTitle={orgJobTitle}
                      PeopleTypesManaged={jobDetails.PeopleTypesManaged}
                      promoteEmployees={jobDetails.promoteEmployees}
                      reports={reports}
                      reviewPerformance={jobDetails.reviewPerformance}
                      saveJob={actions.saveJob}
                      saveJobAndUpdatePay={actions.saveJobAndUpdatePay}
                      setPay={jobDetails.setPay}
                      terminateEmployees={jobDetails.terminateEmployees}
                      updatePayReport={actions.updatePayReport}
                      workLocation={workLocation}
                      workLocationExact={workLocationExact}
                    />
                  )}
                </section>
              </React.Fragment>
            )}
            <section className="slideout-subheader">
              {(showJobCards || showJobSearch) && (
                <h3 className="slideout-subheader__title">PayScale crowd-sourced match</h3>
              )}
              {showJobCards && (
                <h4 className="slideout-subheader__action-container">
                  <div>
                    <span className="slideout-subheader__prompt">Matches don't look right to you?</span>
                    <a className="slideout-subheader__action" onClick={this.toggleCards}>
                      Search for a different job title
                    </a>
                  </div>
                  {showJobCards && selectedJobTitle && (
                    <a className="slideout-subheader__action" onClick={this.toggleDetails} id="cancel-job-search">
                      Cancel
                    </a>
                  )}
                </h4>
              )}
              {showJobSearch && (
                <JobTitleSearch
                  cancelEditJobTitle={this.toggleSearch}
                  editJobTitleMode={true}
                  feature="crowd_source_match"
                  isMP={isMP}
                  jobCode={orgJobCode}
                  noJobCode={false}
                  orgJobId={orgJobId}
                  orgJobTitle={null}
                  titleJobSearch={actions.titleJobSearch}
                  toggleCards={this.toggleCards}
                  workLocationExact={selectedWorkLocation}
                />
              )}
            </section>
            {showJobCards && (
              <section>
                <JobTitleMatches
                  addJob={{
                    editJobTitleMode,
                    jobCode: orgJobCode,
                    jobDescription,
                    jobDetails,
                    noJobCode,
                    orgJobId,
                    orgJobTitle,
                    searchLoaded,
                    searchLoading,
                    selectedJobTitle,
                    workLocationExact,
                    orgJobCodeKey
                  }}
                  app={window.app}
                  feature="crowd_source_match"
                  getAllReports={actions.getAllReports}
                  getJobCerts={actions.getJobCerts}
                  getJobSkills={actions.getJobSkills}
                  handleJobClick={this.handleJobClick}
                  isMP={isMP}
                  items={jobTitles}
                  saveJob={actions.saveJob}
                  selectJob={actions.selectJob}
                  setInitialJobDetails={actions.setInitialJobDetails}
                />
              </section>
            )}
          </div>
        </React.Fragment>
      </CommonErrorBoundary>
    );
  }
}

const mapStateToProps = ({
  addJob,
  careerLevels,
  competitiveSets,
  jobCertifications,
  jobDetails,
  jobSurveyList,
  session,
  skillGeoDifferential,
  skillsByJobList,
  suggestCareer
}) => ({
  careerLevel: addJob.careerLevel,
  careerLevels: careerLevels.items,
  compSetsList: competitiveSets.items,
  compSetsLoaded: competitiveSets.loaded,
  compSetsLoading: competitiveSets.loading,
  currentEditSet: competitiveSets.currentEditSet,
  editJobTitleMode: addJob.editJobTitleMode,
  formSections: {
    showEducation: addJob.formSections.education.display,
    showExperience: addJob.formSections.experience.display,
    showResponsibilities: addJob.formSections.responsibilities.display,
    showSkillsCerts: addJob.formSections.skillsByJob.display
  },
  HighestDegreeEarnedList: addJob.HighestDegreeEarnedList,
  jobCertifications: {
    allCerts: jobCertifications.allCerts,
    certsSearchItems: jobCertifications.certsSearchItems
  },
  jobDescription: addJob.jobDescription,
  jobDetails: addJob.jobDetails,
  jobIdList: jobDetails.jobIdList,
  jobSurveyMatchList: jobSurveyList.items,
  jobSurveyMatchLoaded: jobSurveyList.loaded,
  jobSurveyMatchLoading: jobSurveyList.loading,
  jobTitles: addJob.jobTitles,
  noJobCode: addJob.noJobCode,
  orgTypes: competitiveSets.orgTypes,
  saveProfilesAnalyzedDisplay: jobDetails.details?.payscale_job_info?.saveProfilesAnalyzed,
  reports: addJob.reports,
  savingCompSet: competitiveSets.saving,
  searchLoaded: addJob.searchLoaded,
  searchLoading: addJob.searchLoading,
  selectedJobId: addJob.selectedJobId,
  selectedJobTitle: addJob.selectedJobTitle,
  skillsByJobList: {
    allSkills: skillsByJobList.allSkills,
    skillsSearchItems: skillsByJobList.skillsSearchItems
  },
  subsite: session.organization.code,
  suggestCareer: suggestCareer.items,
  replacementTokens: session.replacementTokens,
  useJobCodeKey: session.organization.useJobCodeKey,
  validation: competitiveSets.validation,
  workLocation: addJob.workLocation,
  workLocationExact: addJob.workLocationExact
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      assignCompetitiveSet,
      cancelEditJobTitle,
      certificationsSearch,
      changeSetValue,
      clearAddJobState,
      closeEditSet,
      deleteMatch,
      deletePayscaleJobDetails,
      deleteSet,
      editJobSlideoutLoad,
      editSet,
      getAllReports,
      getCompSets,
      getIndustryOptions,
      getJobCerts,
      getJobSkills,
      getOrgType,
      jobLocationSearch,
      launchEditJobSlideout,
      loadJobDetails,
      saveCareerLevel,
      saveJob,
      saveJobAndUpdatePay,
      saveProfilesAnalyzed,
      saveSet,
      selectJob,
      setInitialJobDetails,
      showEditSurveys,
      skillsSearch,
      surveyPriceLocation,
      titleJobSearch,
      updatePayReport
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CrowdSourcedMatch));
